"use client";

import { Suspense } from "react";
import { usePathname } from "next/navigation";
import { Overview } from "@/components/finance/pages/Overview";
import { NetWorth } from "@/components/finance/pages/NetWorth";
import { Transactions } from "@/components/finance/pages/Transactions";
import { Trends } from "@/components/finance/pages/Trends";
import { CashFlow } from "@/components/finance/pages/CashFlow";
import { Savings } from "@/components/finance/pages/Savings";
import { Accounts } from "@/components/finance/pages/Accounts";
import { AccountDetail } from "@/components/finance/pages/AccountDetail";
import { Projections } from "@/components/finance/pages/Projections";
import { ImportConsole } from "@/components/finance/pages/ImportConsole";

/**
 * The finance app's single client-side router. Every /finance/* URL is served
 * by ONE catch-all route rendering this component, which switches on the
 * pathname — so moving between finance pages is a history.pushState (see
 * lib/clientNav.ts), not a router navigation: no RSC payload, no prefetch,
 * no network. Next's native-History-API interop keeps usePathname /
 * useSearchParams in sync, so the pages' own URL-state code (Trends drills,
 * Transactions filters, range snapping) is untouched.
 */
export function FinanceShell() {
  const pathname = usePathname();
  const seg = pathname.replace(/^\/finance\/?/, "").split("/").filter(Boolean);
  const page = seg[0] ?? "";

  if (page === "accounts" && seg[1]) return <AccountDetail accountId={decodeURIComponent(seg[1])} />;
  switch (page) {
    case "":
      return <Overview />;
    case "net-worth":
      return <NetWorth />;
    case "transactions":
      return (
        <Suspense>
          <Transactions />
        </Suspense>
      );
    case "trends":
      return (
        <Suspense>
          <Trends />
        </Suspense>
      );
    case "cash-flow":
      return <CashFlow />;
    case "savings":
      return <Savings />;
    case "accounts":
      return <Accounts />;
    case "projections":
      return <Projections />;
    case "import":
      return <ImportConsole />;
    default:
      return <Overview />;
  }
}
